// cloudFunctions/functions/schema/teamsAndMembersSchema.ts
import { z as z2 } from "zod";

// cloudFunctions/functions/migrationUtils/soccer_teams.utils.ts
import { z } from "zod";
async function checkFSlicenseEndDate(team, rest, _uniqueEndDates, ctx, fs) {
  if (!fs) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "no firestore instance passed to mergeEndDates"
    });
    return;
  }
  const license = await fs.collection("licenses").doc(team.attached_license).get();
  let endDate = null;
  if (!license.exists) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `license "${team.attached_license}" attached to ${team.id} does not exist`
    });
  } else {
    endDate = license.data()?.expiration_date;
  }
  if (!endDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `no end date found for license ${team.attached_license} in licenses collection attached to ${team.id}`
    });
  }
  return { ...rest, license_endDate: endDate };
}
function isZodIssueUnrecognizedKeys(issue) {
  return issue.code === z.ZodIssueCode.unrecognized_keys;
}
function removeUnrecognizedFields(team, unrecognizedFields, ctx) {
  try {
    for (const field of Object.keys(team)) {
      if (unrecognizedFields.includes(field)) {
        delete team[field];
      }
    }
    return team;
  } catch (error) {
    if (!ctx) {
      console.error(`error removing unrecognized fields for team ${team.id}: ${error}`);
      return void 0;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `error removing unrecognized fields for team ${team.id}: ${error}`
    });
    return void 0;
  }
}
function reAssignLimitToDefaultPlayerSlots(team, ctx) {
  try {
    if (Object.hasOwn(team, "limit")) {
      team.default_player_slots = Number.isNaN(team.limit) ? 30 : Number.parseInt(team.limit);
      delete team.limit;
      return team;
    }
    return team;
  } catch (e) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `error reassigning limit to default_player_slots for team ${team.id}: ${e}`
    });
  }
}
function normalizeJoinRequests(team, ctx) {
  try {
    let { joinRequests, join_requests, teamRequests, ...rest } = team;
    if (join_requests) {
      joinRequests = join_requests;
    }
    if (teamRequests) {
      joinRequests = teamRequests;
    }
    if (!joinRequests) {
      return { ...rest, joinRequests: 0 };
    }
    if (typeof joinRequests === "object") {
      for (const key of Object.keys(joinRequests)) {
        joinRequests[key] = Number.parseInt(joinRequests[key]);
      }
      return { ...rest, joinRequests };
    }
  } catch (e) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `error normalizing join requests for team ${team.id}: ${e}`
    });
  }
}
function mergeAttachedLicenses(team, ctx) {
  try {
    const { attached_license, license, ...rest } = team;
    let licenseId = null;
    if (typeof attached_license === "string" && typeof license === "string" && attached_license === license) {
      return { attached_license, ...rest };
    }
    if (attached_license === false && license === false) {
      return { attached_license, ...rest };
    }
    if (attached_license === "license_id" || license === "license_id") {
      return { attached_license: false, ...rest };
    }
    if (attached_license !== license) {
      if (typeof attached_license === "string") {
        licenseId = attached_license;
      }
      if (typeof license === "string") {
        licenseId = license;
      }
    }
    if (licenseId) {
      return { attached_license: licenseId, ...rest };
    }
    if (!licenseId) {
      return { ...rest, attached_license: false };
    }
  } catch (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `error merging attached licenses for team ${team.id}: ${error}`
    });
  }
  return void 0;
}
function notEmpty(value) {
  const emptyValues = [null, void 0, "", 0, false];
  return !emptyValues.includes(value);
}
async function mergeEndDates(team, ctx, fs = null) {
  const { license_endDate, expiration_date, license_expiration, ...rest } = team;
  try {
    const dates = [license_endDate, expiration_date, license_expiration];
    const uniqueEndDates = [...new Set(dates.filter((d) => notEmpty(d)))];
    if (uniqueEndDates.length === 1) {
      return { ...rest, license_endDate: uniqueEndDates[0] };
    }
    if (!uniqueEndDates.length) {
      if (!team.attached_license) {
        return { ...rest, license_endDate: false };
      }
      if (fs) {
        return await checkFSlicenseEndDate(team, rest, uniqueEndDates, ctx, fs);
      }
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `no license end date for license ${team.attached_license}: ${license_endDate} expiration_date: ${expiration_date} license_expiration: ${license_expiration}`
      });
    }
    if (uniqueEndDates.length > 1) {
      console.log(`team ${team.id} has multiple end dates, have to check have FS`);
      return await checkFSlicenseEndDate(team, rest, uniqueEndDates, ctx, fs);
    }
    return void 0;
  } catch (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `error merging end dates for team ${team.id}: ${error}`
    });
    return { ...rest, license_endDate: null };
  }
}
function removeNaAndDuplicatesInMembersArrays(team, ctx) {
  try {
    const { player_ids, coaching_staff_ids, ...rest } = team;
    if (Array.isArray(player_ids) && Array.isArray(coaching_staff_ids)) {
      return {
        ...rest,
        player_ids: [...new Set(player_ids?.filter((id) => id !== "n/a"))],
        coaching_staff_ids: [...new Set(coaching_staff_ids?.filter((id) => id !== "n/a"))]
      };
    }
    let result;
    if (Array.isArray(player_ids)) {
      result = {
        ...rest,
        player_ids: [...new Set(player_ids?.filter((id) => id !== "n/a"))]
      };
    } else {
      result = { ...rest, player_ids: [] };
    }
    if (Array.isArray(coaching_staff_ids)) {
      result = {
        ...result,
        coaching_staff_ids: [...new Set(coaching_staff_ids?.filter((id) => id !== "n/a"))]
      };
    } else {
      result = { ...result, coaching_staff_ids: [] };
    }
    return result;
  } catch (error) {
    const teamId = team?.id ?? "unknown";
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `error removing n/a and duplicates in team ${teamId} members arrays: ${error}`
    });
    return { ...team, player_ids: [], coaching_staff_ids: [] };
  }
}
function removePlayerPurchaseSlotsDuplicates(team, ctx) {
  try {
    const { additional_player_slots, additional_player_slots_purchased, ...rest } = team;
    let val;
    if (additional_player_slots !== additional_player_slots_purchased) {
      val = Math.max(Number.parseInt(additional_player_slots), Number.parseInt(additional_player_slots_purchased));
    }
    if (Number.isNaN(val)) {
      val = additional_player_slots || additional_player_slots_purchased;
    }
    if (String(val).toString() === "n/a" || !val) {
      val = 0;
    }
    return {
      ...rest,
      additional_player_slots: val
    };
  } catch (error) {
    console.warn("error removing player purchase slots duplicates", error);
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "error removing player purchase slots duplicates"
    });
    return { ...team, additional_player_slots: 0 };
  }
}

// cloudFunctions/functions/schema/teamsAndMembersSchema.ts
var dateLike = z2.union([z2.string().regex(/^\d{4}-\d{2}-\d{2}/), z2.number(), z2.date(), z2.literal(false)]);
var toDate = dateLike.transform((value) => {
  if (typeof value === "number") {
    const date2 = new Date(value);
    return Number.isNaN(date2.getTime()) ? value : value;
  }
  if (typeof value === "object" && value === null) {
    return null;
  }
  if (value === false) {
    return false;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString().split("T")[0];
});
var teamLikeTransformer = (fs = null) => {
  return z2.unknown().transform(async (team, ctx) => {
    const joinRequestsNormalized = normalizeJoinRequests(team, ctx);
    const mergedLicenses = mergeAttachedLicenses(joinRequestsNormalized, ctx);
    if (!mergedLicenses) {
      throw new Error(`unable to merge licenses for team ${team.id}`);
    }
    let mergedDates;
    if (!fs) {
      mergedDates = await mergeEndDates(mergedLicenses, ctx);
    } else {
      mergedDates = await mergeEndDates(mergedLicenses, ctx, fs);
    }
    if (!mergedDates) {
      throw new Error(`unable to merge dates for team ${team.id}`);
    }
    const deduped = removeNaAndDuplicatesInMembersArrays(mergedDates, ctx);
    if (!deduped) {
      throw new Error(`unable to dedupe team ${team.id}`);
    }
    const reAssignedLimit = reAssignLimitToDefaultPlayerSlots(deduped, ctx);
    if (!reAssignedLimit) {
      throw new Error(`unable to reassign limit for team ${team.id}`);
    }
    const slotDeDuped = removePlayerPurchaseSlotsDuplicates(reAssignedLimit, ctx);
    if (!slotDeDuped) {
      throw new Error(`unable to remove player purchase slots duplicates for team ${team.id}`);
    }
    let res;
    try {
      res = teamSchema.parse(slotDeDuped);
    } catch (e) {
      console.warn("removing unrecognized fields", e);
      if (e instanceof z2.ZodError) {
        const [fieldError] = e.errors.filter((e2) => e2.code === "unrecognized_keys");
        if (fieldError && isZodIssueUnrecognizedKeys(fieldError)) {
          res = removeUnrecognizedFields(slotDeDuped, fieldError.keys, ctx);
        }
      }
    }
    if (!res) {
      throw new Error(`unable to remove unrecognized fields for team ${team.id}`);
    }
    return res;
  });
};
var teamSchema = z2.object({
  country: z2.string().min(1).max(128).nullish(),
  league: z2.string().min(1).max(128).nullish(),
  gkJerseyColor: z2.number().nullish(),
  gkNumberColor: z2.number().nullish(),
  jerseyColor: z2.number().nullish(),
  numberColor: z2.number().nullish(),
  allowFeedback: z2.boolean().nullish().describe("allow feedback on team's events; if doesn't exist, default to false"),
  leaderboardVisible: z2.boolean().nullish().describe("allow players to see leaderboards; if doesn't exist, default to false"),
  showSquadRank: z2.boolean().nullish().describe(
    "coaches grade players - if true, allow players to see the team average and their own personal score; if doesn't exist, default to false;; when the event is over it's stored in rtdb/eventForm on event basis"
  ),
  hideReadiness: z2.boolean().nullish().describe(
    "coaches might prefer to hide players' readiness from them; if doesn't exist, default to false aka is visible to players"
  ),
  showPlayerForm: z2.array(z2.string()).nullish().describe(
    "if false - coaches disallow players see their own form; if the field doesn't exist, players cannot see their scores from the coach (opposite of can see readiness); it's on a per coach basis, meaning the setting can differ from coach to coach (teamID/CoachID/isShown)"
  ),
  indivMessengerSetting: z2.boolean().nullish().describe(
    "if false or doesn't exist - coaches cannot send messages to individual players, only to the team chat"
  ),
  teamSettingsForParents: z2.object({
    allowsParents: z2.boolean().nullish().default(false),
    canJointTeamChat: z2.boolean().nullish().default(false),
    canMessageStaff: z2.boolean().nullish().default(false),
    canModifyAttendanceTeam: z2.boolean().nullish().default(false),
    canViewDevelopmentTeam: z2.boolean().nullish().default(false),
    canViewLeaderboard: z2.boolean().nullish().default(false),
    canViewPerformanceTeam: z2.boolean().nullish().default(false)
  }).nullish().describe("if false - parents cannot have any access to a player's team"),
  id: z2.string().min(1).max(128),
  creatorId: z2.string().min(1).max(128),
  creator_email: z2.string().refine(
    (value) => {
      const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return emailPattern.test(value);
    },
    { message: "Invalid creator email address" }
  ).nullish(),
  // TODO: remove nullish
  coachName: z2.string(),
  date_created: toDate.nullish().default(null),
  attached_license: z2.union([
    z2.string().min(1).refine(
      (str) => {
        return str !== "license_id";
      },
      { message: "license_id is not a valid license" }
    ),
    z2.boolean()
  ]),
  license_endDate: toDate.nullish().default(null).catch(null),
  additional_coach_slots: z2.number().default(0),
  additional_coach_slots_package_info: z2.object({
    purchaseId: z2.string().nullish().default(null)
  }).nullish(),
  additional_player_slots: z2.number().min(0).default(0),
  default_coach_slots: z2.number().refine((val) => val === 3).default(3),
  default_player_slots: z2.number().default(30).catch(30),
  // limit: z.number().default(30).catch(30),
  coaching_staff_ids: z2.array(z2.string()).min(1),
  player_ids: z2.array(z2.string()).nullish(),
  imgUrl: z2.string().url().catch(
    "https://firebasestorage.googleapis.com/v0/b/soccerfit-36cce.appspot.com/o/teamImg%403x.png?alt=media&token=dd1c3c35-aa58-492c-b87c-4f581159f708"
  ),
  teamName: z2.string().min(1).max(128),
  source: z2.string().refine((val) => ["ios", "android", "web"].includes(val.toLowerCase())).default("iOS").catch("iOS"),
  teamAge: z2.enum([
    "U-10",
    "U-11",
    "U-12",
    "U-13",
    "U-14",
    "U-15",
    "U-16",
    "U-17",
    "U-18",
    "U-19",
    "U-20",
    "U-21",
    "U-23",
    "College",
    "Pro"
  ]),
  joinId: z2.string().refine((val) => /^[A-Z0-9]{6}$/.test(val), {
    message: "Must contain only uppercase letters, and numbers; 6 characters long"
  }),
  pin: z2.string().regex(/^\d{5}$/).catch("00000"),
  subscription_status: z2.enum([
    "incomplete",
    "incomplete_expired",
    "trialing",
    "active",
    "past_due",
    "canceled",
    "unpaid",
    "inactive"
    // comes from getAllUserTeams
  ]).describe(
    'defined by Stripe api https://stripe.com/docs/api/subscriptions/object usually we get "active" or ")trialing"; "inactive" is a custom status set in getallUserTeams cloud function'
  ).nullish(),
  // TODO: remove nullish
  tier: z2.string().nullish().default("gold/silver/bronze"),
  joinRequests: z2.union([z2.literal(0), z2.record(toDate).or(z2.literal(1))]).catch(0),
  magicbell_user_ids: z2.record(z2.string()).nullish().describe("firebaseUUID: magicbellUserId")
}).passthrough();
var baseTeamSchema = teamSchema.pick({
  teamAge: true,
  coachName: true,
  creatorId: true,
  imgUrl: true,
  pin: true,
  joinId: true,
  teamName: true,
  date_created: true,
  source: true,
  magicbell_user_ids: true
}).strict();
var extendedBaseTeamSchema = teamSchema.pick({
  license_endDate: true,
  attached_license: true,
  country: true,
  league: true,
  gkJerseyColor: true,
  gkNumberColor: true,
  jerseyColor: true,
  numberColor: true
}).merge(baseTeamSchema).passthrough();
var teamMemberSchema = z2.object({
  firstName: z2.string().min(1).max(128).catch("unknown"),
  lastName: z2.string().min(1).max(128).catch("unknown"),
  imgUrl: z2.string().url().optional().default(
    "https://firebasestorage.googleapis.com/v0/b/soccerfit-36cce.appspot.com/o/defaultProfileImg.png?alt=media&token=6ae0f86d-faf5-41e7-8551-1ce2efdb04c7"
  ).catch(
    "https://firebasestorage.googleapis.com/v0/b/soccerfit-36cce.appspot.com/o/defaultProfileImg.png?alt=media&token=6ae0f86d-faf5-41e7-8551-1ce2efdb04c7"
  ),
  uid: z2.string().min(1).max(128),
  activeInjuryKey: z2.string().min(1).max(128).nullish().default(null),
  activeInjuryEndDate: toDate.nullish().default(null),
  position: z2.string().nullish().default("winger").catch(null),
  coachOrPlayer: z2.enum(["Coach", "Player"]),
  joinDate: toDate.nullish().catch(null),
  bpEmail: z2.string().email().nullish().default(null).catch(null)
});
var teamMembersSchema = z2.map(
  z2.string(),
  z2.object({
    players: z2.record(teamMemberSchema).nullish(),
    coaching_staff: z2.record(teamMemberSchema).nullish()
  })
);
var teamMemberLikeTransformer = z2.unknown().transform((teamMember) => {
  const { id, uid, uuid, ...rest } = teamMember;
  const oldID = id || uuid;
  const idVal = uid || oldID;
  if (oldID) {
    console.warn("teamMemberSchema: id & uuid is deprecated, use uid instead for teamMember: ", idVal);
  }
  return { ...rest, uid: idVal };
});
var teamWithMembersSchema = teamSchema.extend({
  players: z2.record(teamMemberSchema).nullish(),
  coaching_staff: z2.record(teamMemberSchema).nullish()
});
async function applyTeamSchema(team, fs = null) {
  let mergedTeam;
  let validatedTeam;
  const teamId = team?.id;
  if (!teamId) {
    throw Object.assign(new Error("unable to read team.id - id is required"), team);
  }
  if (!fs) {
    mergedTeam = await teamLikeTransformer().parseAsync(team);
  } else {
    mergedTeam = await teamLikeTransformer(fs).parseAsync(team);
  }
  validatedTeam = teamSchema.parse(mergedTeam);
  const jsonStr = JSON.stringify(validatedTeam);
  let byteSize;
  if (typeof Buffer === "undefined") {
    const encoder = new TextEncoder();
    const encodedStr = encoder.encode(jsonStr);
    byteSize = encodedStr.byteLength;
  } else {
    const buff = Buffer.from(jsonStr, "utf8");
    byteSize = buff.byteLength;
  }
  if (byteSize >= 1048576) {
    throw new Error(
      `team ${teamId} is larger than 1MiB per Firestore document size limit; see https://firebase.google.com/docs/firestore/quotas#collections_documents_and_fields`
    );
  }
  return validatedTeam;
}
function applyTeamMemberSchema(teamMember, error = null) {
  if (!error) {
    error = console.error;
  }
  try {
    const modifiedTeamMember = teamMemberLikeTransformer.parse(teamMember);
    const validatedTeamMember = teamMemberSchema.parse(modifiedTeamMember);
    return validatedTeamMember;
  } catch (e) {
    error(`error applying teamMemberSchema for user ${teamMember.id}`, e);
    return;
  }
}
var teamsMetadataSchema = z2.object({
  last_modified: z2.object({
    action: z2.string(),
    // "team data update"
    id: z2.string(),
    // "-NY1NlIjbkt5f3mydxno"
    actionBreakdown: z2.object({
      removedDuringUpdate: z2.array(z2.string()).optional(),
      addedDuringUpdate: z2.record(z2.string(), z2.unknown()).optional(),
      updatedProperties: z2.record(z2.string(), z2.unknown()).optional()
    }).and(z2.record(z2.string(), z2.unknown()).optional())
  }),
  total_count: z2.number().int()
});
export {
  applyTeamMemberSchema,
  applyTeamSchema,
  baseTeamSchema,
  extendedBaseTeamSchema,
  teamMemberLikeTransformer,
  teamMemberSchema,
  teamSchema,
  teamsMetadataSchema
};
//# sourceMappingURL=teamsAndMembersSchema.js.map
